import React from "react";
import PropTypes from "prop-types"
import { Card, CardBody, FormGroup, Label, Row, Table } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import RefundModal from "../Modals/RefundModal";
import { useState } from "react";
import { CALLS_STATUS_OPTIONS, getLeadTypeById, LEADS_CALLS_STATUS_DEFAULT_VALUE, LEADS_STATUS_OPTIONS, LEAD_REFUND_MAX_DAYS, PRODUCT_TYPE_IDS, REPORTING_TIMEZONE, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import { capitalize } from "lodash";
import { utcToZonedTime } from "date-fns-tz";
import { addDays, format, isAfter, parseISO } from "date-fns";
import { generateConsentCaptureUrl, getIANATimezoneStringForAbbreviation, getLeadRedundStatus, getPrimaryTextClass } from "helpers/sf_utils";
import { dollarFormatWithTwoDecimalPlaces } from "helpers/sf_utils";
import { useDispatch } from "react-redux";
import { useEffect } from "react";
import LeadEditDropdown from "./LeadEditDropdown";
import { addLeadRefund, updateLead, updateLeadDetails } from "store/leads/actions";
import EditAddressDropdown from "./EditAddressDropdown";
import LeadEditDropdownForNumberFields from "./LeadEditDropdownForNumberFields";
import EditUsernameDropdown from "./EditUsernameDropdown";
import { updateLeadApplicant } from "store/leads/lead_applicants_actions";
import ReactAudioPlayer from "react-audio-player";
import { isWithinInterval } from 'date-fns';
import useFeatureFlags from "hooks/useFeatureFlags";
import LabelWithTooltip from "../LabelWithTooltip";
import { addProspectRefund, updateProspect, updateProspectDetails } from "store/prospects/actions";
import { updateProspectApplicant } from "store/prospects/prospects_applicants_actions";
import useReceiptTypes from "hooks/useReceiptTypes";
import { isInsuranceioBrand, isBenepathBrand } from "config";
import { getNotesCountText as getNotesCountTextHelper } from "helpers/sf_lead_notes_helper";
import { bsh_isAdminUserDetailsPresent } from "helpers/sf_browser_storage_helper";

export const getCostFromLeadReceipts = (leadReceipts, leadCreatedAt, isReceiptTypeIdDebitActual) => {
    if (!leadReceipts) return 0;

    let sum = 0;
    let leadCreatedAtDate = null;

    try {
        leadCreatedAtDate = parseISO(leadCreatedAt);
    } catch (e) {
    }

    // BM-2897 if the lead was created before the date, then just take first receipt amount
    // since previous logic stored debit as lead cost for all receipts
    if (leadCreatedAtDate && isAfter(leadCreatedAtDate, parseISO('2024-06-10T07:00:00.000Z'))) {
        // Sum of debit of all debit receipts
        sum = leadReceipts.reduce((accum, cur_receipt) => (
            isReceiptTypeIdDebitActual(cur_receipt?.receipt_type_id) ? accum + (parseFloat(cur_receipt?.debit)) : accum
        ), 0);
    } else {
        //Return cost as the first receipt debit amount where receipt_type_id is 1
        sum = leadReceipts.reduce((accum, cur_receipt) => (
            isReceiptTypeIdDebitActual(cur_receipt?.receipt_type_id) ? (parseFloat(cur_receipt?.debit)) : accum
        ), 0);
    }
    return sum
}

const LeadSnapshot = ({ current_account, lead_data, lead_refund_reasons, show_recording_for_prospect, is_prospect, onClickOpenNotes, lead_notes, call_notes, isCallDetailsPage }) => {
    const isLeadMarketplace = lead_data?.product_type_id === PRODUCT_TYPE_IDS.LEADS && is_prospect;
    const [refundModal, setRefundModal] = useState({ active: false, values: null });
    const { lead_id } = useParams();
    const [enableRefund, setEnableRefund] = useState(false);
    const dispatch = useDispatch();
    const { isReceiptTypeIdDebitActual } = useReceiptTypes()

    const [isFeatureFlagActive] = useFeatureFlags();
    let leadUpdater = updateLead
    let refundAdder = addLeadRefund
    let leadApplicantUpdater = updateLeadApplicant
    let detailsUpdater = updateLeadDetails
    if (is_prospect) {
        leadUpdater = updateProspect;
        refundAdder = addProspectRefund
        leadApplicantUpdater = updateProspectApplicant
        detailsUpdater = updateProspectDetails
    }

    const new_consent_ui_ff = isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ENABLE_CUSTOM_CONSENT_UI)
    useEffect(() => {
        if (!lead_data?.created_at || !current_account) return;

        const currentTimeUTC = new Date();
        const leadDataCreatedAt = new Date(lead_data.created_at);
        const isWithinRefundPeriod = isWithinInterval(currentTimeUTC, {
            start: leadDataCreatedAt,
            end: addDays(leadDataCreatedAt, LEAD_REFUND_MAX_DAYS),
        });
        setEnableRefund((current_account?.enterprise === false) && isWithinRefundPeriod);
    }, [lead_data, current_account]);


    const launchRefundModal = (active, values) => {
        setRefundModal({ active: true, values: values });
    };

    // Save username
    const onSubmitUsername = (values) => {
        const userData = {
            lead_details: {
                id: lead_data?.lead_detail?.id,
                first_name: values?.first_name,
                last_name: values?.last_name,
            }
        }
        dispatch(detailsUpdater(userData));
        //For updating the name of primary lead applicant/driver
        const usernameData = {
            id: lead_data?.lead_applicants[0]?.id,
            first_name: values.first_name,
            last_name: values.last_name
        }
        dispatch(leadApplicantUpdater({ lead_applicant: usernameData }))
    }

    // Save address
    const onSubmitAddress = (values) => {
        const data = {
            lead_details: {
                id: lead_data?.lead_detail?.id,
                state: values?.state,
                address: values?.address,
                city: values?.city,
                zip: values?.zip
            }
        }
        dispatch(detailsUpdater(data));
    }

    //Save Refund Request
    const onSubmitRefundRequest = (values) => {
        dispatch(refundAdder({ ...values, lead_id, "status": "Initiated" }));
        setRefundModal({ active: false, values: null })
        return
    }

    // Get notes count text
    const getNotesCountText = () => {
        return getNotesCountTextHelper({
            call_notes,
            lead_notes,
            call_id: lead_data?.quote_call?.id,
            lead_id: lead_data?.id,
            isCallDetailsPage
        });
    }

    const recordingSrc = lead_data?.product_type_id === PRODUCT_TYPE_IDS.QUOTE_FUNNELS ? lead_data?.qf_quote_call?.recording_url : lead_data?.quote_call?.recording_url;
    return (
        lead_data?.lead_detail ? <React.Fragment>
            <RefundModal
                onRefundClick={onSubmitRefundRequest}
                onCloseClick={() => setRefundModal({ active: false, values: null })}
                show={refundModal}
                title='Request Refund'
                refund_requested_for={lead_data?.lead_detail?.first_name || (lead_data?.lead_detail?.last_name ? "" : "--") + ' ' + lead_data?.lead_detail?.last_name || (lead_data?.lead_detail?.first_name ? "" : "--")}
                phone_of_refund_requested_for={lead_data?.lead_detail?.phone}
            />
            <Card>
                <CardBody className="py-0">
                    <Row id="lead-snapshot-table">
                        <div className="px-0">
                            <Table className="mb-0 text-black">
                                <tbody>
                                    <tr>
                                        <td className="h4 font-weight-semibold text-black">
                                            <div>
                                                <div className="d-flex align-items-top">
                                                    <EditUsernameDropdown
                                                        first_name={lead_data?.lead_detail?.first_name || (lead_data?.lead_detail?.last_name ? "" : "--")}
                                                        last_name={lead_data?.lead_detail?.last_name || (lead_data?.lead_detail?.first_name ? "" : "--")}
                                                        onSaveUsernameClick={onSubmitUsername}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    {(lead_data?.product_type_id === 2 || show_recording_for_prospect) &&
                                        <tr>
                                            <td>
                                                <div>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Call Recording
                                                    </Label>
                                                    <div className="d-flex align-items-center mt-2">
                                                        {recordingSrc ?
                                                            <ReactAudioPlayer
                                                                src={recordingSrc}
                                                                style={{ height: '30px', width: '100%' }}
                                                                controls={true}
                                                                onPlay={() => { }}
                                                                onPause={() => { }}
                                                            />
                                                            :
                                                            'No recording available'
                                                        }
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Phone
                                                </Label>
                                                <div className="d-flex align-items-center">
                                                    <LeadEditDropdownForNumberFields
                                                        isPhoneNumber={true}
                                                        title='Phone'
                                                        field={lead_data?.lead_detail?.phone}
                                                        onSave={(value) => {
                                                            let data = {}
                                                            data.id = lead_data?.lead_detail?.id
                                                            data.phone = value
                                                            dispatch(detailsUpdater({ lead_details: data }))
                                                        }}
                                                        onCancel={() => { }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Email
                                                </Label>
                                                <div className="d-flex align-items-center">
                                                    <LeadEditDropdown
                                                        title='Email'
                                                        field={lead_data?.lead_detail?.email}
                                                        onSave={(value) => {
                                                            let data = {}
                                                            data.id = lead_data?.lead_detail?.id
                                                            data.email = value
                                                            dispatch(detailsUpdater({ lead_details: data }))
                                                        }}
                                                        onCancel={() => { }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Address
                                                </Label>
                                                <div className="d-flex align-items-top">
                                                    <EditAddressDropdown
                                                        Address={lead_data?.lead_detail?.address}
                                                        State={lead_data?.lead_detail?.state}
                                                        City={lead_data?.lead_detail?.city}
                                                        ZipCode={lead_data?.lead_detail?.zip}
                                                        onSaveAddressClick={onSubmitAddress}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ cursor: 'default' }}>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Type
                                                </Label>
                                                <p className="mb-0">{capitalize(getLeadTypeById(lead_data?.lead_type_id)?.name) || '--'}</p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Campaign
                                                </Label>
                                                <p className="mb-0">
                                                    <Link to={`/campaigns/${lead_data?.campaign?.id}/summary`} className='text-sfblue'>
                                                        {lead_data?.campaign?.name}
                                                    </Link>
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ cursor: 'default' }}>
                                            {lead_data?.product_type_id === 2 ?
                                                <div>

                                                    <Label className="mb-0 font-weight-semibold">
                                                        Call Received
                                                    </Label>
                                                    <p className="mb-0">{lead_data?.quote_call?.created_at ? format(utcToZonedTime((lead_data?.quote_call?.created_at || lead_data?.lead_detail?.created_at), getIANATimezoneStringForAbbreviation(REPORTING_TIMEZONE)), 'MM/dd/yyyy hh:mm aa') : '--'}</p>
                                                </div> :
                                                <div>

                                                    <Label className="mb-0 font-weight-semibold">
                                                        {is_prospect ? 'Prospect' : 'Lead'} Received
                                                    </Label>
                                                    <p className="mb-0">{format(utcToZonedTime(lead_data?.lead_detail?.created_at, getIANATimezoneStringForAbbreviation(REPORTING_TIMEZONE)), 'MM/dd/yyyy hh:mm aa')}</p>
                                                </div>
                                            }

                                        </td>
                                    </tr>
                                    {(!is_prospect || isLeadMarketplace) && <tr>
                                        <td style={{ cursor: 'default' }}>
                                            <div>
                                                <Label className="mb-0 font-weight-semibold">
                                                    Cost
                                                </Label>
                                                <p className="mb-0">{dollarFormatWithTwoDecimalPlaces(getCostFromLeadReceipts(lead_data?.receipts, lead_data?.created_at, isReceiptTypeIdDebitActual))}</p>
                                            </div>
                                        </td>
                                    </tr>}
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_STATUS_COLUMN_IN_LEADS_AND_CALLS) && <tr>
                                        <td>
                                            <div>
                                                <LabelWithTooltip name="Disposition" tooltip_message={`Allows you to categorize the ${lead_data?.product_type_id === PRODUCT_TYPE_IDS.LEADS ? "lead" : "call"} based on where you are in the sales process with the prospect.`} target={`${lead_data?.product_type_id === PRODUCT_TYPE_IDS.LEADS ? "lead" : "call"}-dispotion-tooltip`} htmlFor="macro_key" />
                                                <div className="d-flex align-items-center">
                                                    <LeadEditDropdown
                                                        isDropdown={true}
                                                        menuPlacement='top'
                                                        options={lead_data?.product_type_id === PRODUCT_TYPE_IDS.LEADS ? LEADS_STATUS_OPTIONS : CALLS_STATUS_OPTIONS}
                                                        defaultValue={
                                                            lead_data?.product_type_id === PRODUCT_TYPE_IDS.LEADS
                                                                ? LEADS_CALLS_STATUS_DEFAULT_VALUE.RECEIVED_NOT_WORKED_YET
                                                                : lead_data?.product_type_id === PRODUCT_TYPE_IDS.QUOTE_FUNNELS
                                                                    ? LEADS_CALLS_STATUS_DEFAULT_VALUE.RECEIVED_NOT_WORKED_YET
                                                                    : LEADS_CALLS_STATUS_DEFAULT_VALUE.TRANSFERRED
                                                        }
                                                        title='Disposition'
                                                        field={lead_data?.disposition}
                                                        onSave={(value) => {
                                                            let data = {}
                                                            data.id = lead_data?.id
                                                            data.disposition = value
                                                            dispatch(leadUpdater(data))
                                                        }}
                                                        onCancel={() => { }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>}
                                    {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_LEAD_AND_CALLS_NOTES) && isBenepathBrand && (
                                        <tr>
                                            <td>
                                                <div>
                                                    <Label className="mb-0 font-weight-semibold">
                                                        Notes
                                                    </Label>
                                                    <p className="mb-0">
                                                        {(isCallDetailsPage && lead_data?.quote_call?.id) || (!isCallDetailsPage && lead_data?.id) ? (
                                                            <a onClick={onClickOpenNotes} className='text-sfblue' style={{ cursor: 'pointer' }}>
                                                                {getNotesCountText()}
                                                            </a>
                                                        ) : (
                                                            '--'
                                                        )}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {/* TODO: Replace SF_FEATURE_FLAG_CONSENT_LINKS_SHOWN with !is_prospect and remove the flag.
                                        Goal renders consent in a separate tab and does not need this sidebar field. */}
                                    {(isLeadMarketplace || (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_CONSENT_LINKS_SHOWN) &&
                                        !(lead_data?.product_type_id === PRODUCT_TYPE_IDS.CALLS && isInsuranceioBrand))) &&
                                        lead_data?.lead_detail?.consent_certificate_id && (
                                            <tr>
                                                <td>
                                                    <div>
                                                        <LabelWithTooltip
                                                            name="Consent"
                                                            tooltip_message={"Proof of consent."}
                                                            target={"lead-view-consent-tooltip"}
                                                            htmlFor="lead_view_consent_tooltip" />
                                                        {
                                                            lead_data?.lead_detail?.consent_certificate_id ?
                                                                <div className="mb-0">
                                                                    <p className="pb-0 mb-0">Consent Confirmation</p>
                                                                    {

                                                                        <a
                                                                            href={generateConsentCaptureUrl(lead_data?.lead_detail?.consent_certificate_id)}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-sfblue"
                                                                        >
                                                                            Playback
                                                                        </a>

                                                                    }
                                                                </div> : ''
                                                        }
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    <div>
                                        {lead_data?.lead_refund ?
                                            <div>
                                                <FormGroup>
                                                    <Label className="mb-0">
                                                        Refund Status:
                                                    </Label>
                                                    <span className={`ms-2 ${getPrimaryTextClass()}`}>
                                                        {
                                                            getLeadRedundStatus(lead_data?.lead_refund?.status, lead_data?.lead_refund?.created_at)
                                                        }
                                                    </span>
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label className="mb-0">
                                                        Reason:
                                                    </Label>
                                                    <span className={`ms-2 ${getPrimaryTextClass()}`}>
                                                        {lead_refund_reasons?.find(e => e.id === lead_data?.lead_refund?.lead_refund_reason_id)?.reason}
                                                    </span>
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label className="mb-0">
                                                        Comments:
                                                    </Label>
                                                    <span className={`ms-2 ${getPrimaryTextClass()}`}>
                                                        {lead_data?.lead_refund?.comments}
                                                    </span>
                                                </FormGroup>

                                            </div>
                                            :
                                            (enableRefund && !is_prospect) ? <Label className="text-sfblue font-weight-semibold">
                                                <a onClick={() => launchRefundModal(true, null)} >
                                                    Request Refund
                                                </a>
                                            </Label> : null
                                        }
                                    </div>
                                    {is_prospect && bsh_isAdminUserDetailsPresent() && (<tr>
                                        <td>
                                            <div>
                                                <LabelWithTooltip
                                                    name="TrustedForm Certificate"
                                                    isAdminSettingIcon={true}
                                                    tooltip_message={"Proof of consent is offered by TrustedForm on all form submissions."}
                                                    target={"lead-view-consent-tooltip"}
                                                    htmlFor="lead_view_consent_tooltip" />
                                                <p className="mb-0">
                                                    {
                                                        lead_data?.trusted_form_share_url ?
                                                            (<Link onClick={() => window.open(`${lead_data.trusted_form_share_url}#legacy_session_replay`, '_blank')} className='text-sfblue'>
                                                                View Consent
                                                            </Link>)
                                                            : '--'
                                                    }
                                                </p>
                                            </div>
                                        </td>
                                    </tr>)}

                                </tbody>
                            </Table>
                        </div>
                    </Row>
                </CardBody>
            </Card>
        </React.Fragment > : null
    )
}

export default LeadSnapshot

LeadSnapshot.propTypes = {
    name: PropTypes.string,
    call_duration: PropTypes.number,
    phone: PropTypes.number,
    email: PropTypes.string,
    address: PropTypes.any,
    type: PropTypes.any,
    call_received: PropTypes.any,
    cost: PropTypes.any
}
